import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUserId } from "./use-current-user";

interface UseTimeTrackerParams {
  moduleId: string;
  courseId: string;
  enabled?: boolean;
}

export function useTimeTracker({ moduleId, courseId, enabled = true }: UseTimeTrackerParams) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const startTimeRef = useRef<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { data: userId } = useCurrentUserId();

  useEffect(() => {
    if (!enabled || !moduleId || !courseId || !userId) return;

    const startSession = async () => {
      try {
        startTimeRef.current = new Date();

        const { data, error } = await (supabase
          .from("module_time_tracking") as any)
          .insert({
            user_id: userId,
            module_id: moduleId,
            course_id: courseId,
            session_start: startTimeRef.current.toISOString(),
            time_spent_seconds: 0
          })
          .select()
          .single();

        if (error) {
          console.warn("Time tracking unavailable:", error.message);
          return;
        }

        setSessionId(data.id);

        intervalRef.current = setInterval(() => {
          updateTimeSpent(data.id);
        }, 30000);
      } catch (error) {
        console.warn("Time tracking error:", error);
      }
    };

    const updateTimeSpent = async (sid: string) => {
      if (!startTimeRef.current) return;

      try {
        const now = new Date();
        const timeSpentSeconds = Math.floor((now.getTime() - startTimeRef.current.getTime()) / 1000);

        await supabase
          .from("module_time_tracking")
          .update({
            time_spent_seconds: timeSpentSeconds,
            session_end: now.toISOString()
          })
          .eq("id", sid);
      } catch (error) {
        console.error("Error in updateTimeSpent:", error);
      }
    };

    startSession();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (sessionId) updateTimeSpent(sessionId);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        startTimeRef.current = new Date();
        if (!intervalRef.current && sessionId) {
          intervalRef.current = setInterval(() => {
            updateTimeSpent(sessionId);
          }, 30000);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (sessionId) updateTimeSpent(sessionId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [moduleId, courseId, enabled, userId]);

  return { sessionId };
}
